import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import toast from 'react-hot-toast';
import { Download, Loader2, FileText } from 'lucide-react';
import { Item } from '@/types';
import { labelService } from '@/services/labelService';
import { labelFileService } from '@/services/labelFileService';

interface LabelTemplate {
  id: string;
  name: string;
  width: number;
  height: number;
  description: string;
  supportsLogo: boolean;
  supportsQR: boolean;
  supportsText: boolean;
}

interface SimpleLabelPrinterProps {
  item: Item;
  isOpen: boolean;
  onClose: () => void;
}

const SimpleLabelPrinter: React.FC<SimpleLabelPrinterProps> = ({ item, isOpen, onClose }) => {
  // State für Templates
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  
  // State für Optionen
  const [includeQR, setIncludeQR] = useState(true);
  const [customText, setCustomText] = useState('');
  
  // State für UI
  const [isLoading, setIsLoading] = useState(false);
  const [previewSvg, setPreviewSvg] = useState<string>('');
  const [actualDimensions, setActualDimensions] = useState<{
    widthPx: number;
    heightPx: number;
    widthMm: number;
    heightMm: number;
  } | null>(null);
  const [originalDimensions, setOriginalDimensions] = useState<{
    widthPx: number;
    heightPx: number;
    widthMm: number;
    heightMm: number;
  } | null>(null);

  // Lade Templates beim Öffnen
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  // Generiere Vorschau wenn Template oder Optionen ändern
  useEffect(() => {
    if (selectedTemplate && isOpen) {
      generatePreview();
    }
  }, [selectedTemplate, includeQR, customText, isOpen]);

  const loadTemplates = async () => {
    try {
      const templatesData = await labelService.getTemplates();
      setTemplates(templatesData);
      if (templatesData.length > 0) {
        // Wähle 24mm Template als Standard (optimal für Brother P950NW)
        const defaultTemplate = templatesData.find(t => t.height === 24) || templatesData[0];
        setSelectedTemplate(defaultTemplate.id);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Templates:', error);
      toast.error('Fehler beim Laden der Label-Templates');
    }
  };

  const generatePreview = async () => {
    if (!selectedTemplate) return;

    try {
      setIsLoading(true);
      const result = await labelService.generatePreviewWithDimensions(item.id, {
        templateId: selectedTemplate,
        includeQR,
        includeLogo: true, // Logo immer aktiv
        customText: customText || undefined,
        printerName: undefined
      });
      
      setPreviewSvg(result.svg);
      setActualDimensions(result.dimensions);
      setOriginalDimensions(result.originalDimensions);
      
      if (result.dimensions) {
        console.log('Tatsächliche Label-Dimensionen (Anzeige):', result.dimensions);
      }
      if (result.originalDimensions) {
        console.log('Ursprüngliche Label-Dimensionen (PNG):', result.originalDimensions);
      }
    } catch (error) {
      console.error('Fehler beim Generieren der Vorschau:', error);
      toast.error('Fehler beim Generieren der Vorschau');
    } finally {
      setIsLoading(false);
    }
  };



  const handleDownloadLbx = async () => {
    if (!selectedTemplate) {
      toast.error('Bitte wählen Sie ein Template aus');
      return;
    }

    try {
      const template = templates.find(t => t.id === selectedTemplate);
      if (!template) {
        throw new Error('Template nicht gefunden');
      }

      // Erstelle Label-Daten für .lbx-Generierung
      const labelData = {
        itemName: item.name,
        sku: item.sku,
        serialNumber: item.serialNumber,
        qrCode: `${window.location.origin}/checkout/${item.id}`,
        customText: customText || undefined,
        template: template,
        options: {
          includeQR,
          includeLogo: true // Logo immer aktiv
        }
      };

      // Generiere .lbx-Datei
      const result = await labelFileService.generateBrotherLbxFile(labelData);
      
      if (result.success && result.fileName && result.fileContent) {
        // .lbx-Dateien sind ArrayBuffer (ZIP-Archive)
        const blob = new Blob([result.fileContent], { type: 'application/zip' });
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success('Brother P-Touch Editor 6 Datei (.lbx) heruntergeladen');
      } else {
        throw new Error(result.message || 'Fehler beim Generieren der .lbx-Datei');
      }
    } catch (error: any) {
      console.error('LBX-Download-Fehler:', error);
      toast.error('Fehler beim Erstellen der .lbx-Datei');
    }
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Brother P950NW Label erstellen - {item.name}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Linke Spalte: Einstellungen */}
          <div className="space-y-6">

            {/* Template-Auswahl */}
            <Card>
              <CardHeader>
                <CardTitle>TZe-Band auswählen</CardTitle>
                <CardDescription>
                  Wählen Sie die Bandbreite Ihres Brother TZe-Bands
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger>
                    <SelectValue placeholder="TZe-Band auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{template.name}</span>
                          <span className="text-sm text-muted-foreground">
                            {template.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Label-Optionen */}
            <Card>
              <CardHeader>
                <CardTitle>Label-Inhalt</CardTitle>
                <CardDescription>
                  Bestimmen Sie, was auf dem Label gedruckt werden soll
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="includeQR"
                    checked={includeQR}
                    onCheckedChange={setIncludeQR}
                  />
                  <Label htmlFor="includeQR">QR-Code einschließen</Label>
                </div>


                <div className="space-y-2">
                  <Label htmlFor="customText">Zusätzlicher Text (optional)</Label>
                  <Input
                    id="customText"
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    placeholder="z.B. Abteilung, Standort..."
                  />
                </div>

              </CardContent>
            </Card>
          </div>

          {/* Rechte Spalte: Vorschau und Aktionen */}
          <div className="space-y-6">
            {/* Vorschau */}
            <Card>
              <CardHeader>
                <CardTitle>Label-Vorschau</CardTitle>
                <CardDescription>
                  So wird Ihr Label auf dem Brother P950NW aussehen
                  {actualDimensions && (
                    <span className="text-xs text-muted-foreground mt-1 block">
                      Tatsächliche Größe: {actualDimensions.widthMm.toFixed(1)}mm × {actualDimensions.heightMm.toFixed(1)}mm
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="ml-2">Vorschau wird generiert...</span>
                  </div>
                ) : previewSvg ? (
                  <div 
                    className="border rounded-lg p-4 bg-white flex items-center justify-center min-h-[120px]"
                    dangerouslySetInnerHTML={{ __html: previewSvg }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-32 text-muted-foreground">
                    Wählen Sie ein TZe-Band für die Vorschau
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Download */}
            <Card>
              <CardHeader>
                <CardTitle>Download</CardTitle>
                <CardDescription>
                  Laden Sie die Label-Datei für Brother P-Touch Editor 6 herunter
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  onClick={handleDownloadLbx}
                  disabled={!selectedTemplate}
                  className="w-full"
                  size="lg"
                >
                  <Download className="w-4 h-4 mr-2" />
                  P-Touch Editor 6 (.lbx)
                </Button>


              </CardContent>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SimpleLabelPrinter;
